import React, { useState } from "react";
import "./MandatoryPurchaseModal.css";
import PaymentSummaryModal from "../paymentSummaryModal/PaymentSummaryModal";
import { useAuth } from "../../context/auth";
import axios from "axios";

const MandatoryPurchaseModal = ({
  isOpen,
  type,
  options = [],
  onClose,
  event,
  onProceed,
  onPayLater,
}) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [quantities, setQuantities] = useState({});
  const [isPaymentSummaryModalOpen, setIsPaymentSummaryModalOpen] =
    useState(false);
  const [isClaimingFreeTicket, setIsClaimingFreeTicket] = useState(false);

  const preferredCurrency = event?.preferredCurrency || "NGN";

  const isAttireRequired = type === "attire";
  const isTicketRequired = type === "ticket";

  const attireOptions = isAttireRequired ? options : [];
  const ticketOptions = isTicketRequired ? options : [];

  const { user } = useAuth();

  const refundPolicy = isAttireRequired
    ? event?.eventAttire?.refundPolicy
    : null;

  const selectedTicket = selectedOptions.find(
    (opt) => opt.purchaseType === "TICKET",
  );

  const isSelectedTicketFree = () => {
    if (!selectedTicket) return false;
    return parseFloat(selectedTicket.price) === 0;
  };

  const handleSelectOption = (option) => {
    setSelectedOptions((prev) => {
      const exists = prev.find((item) => item.attireTag === option.attireTag);

      if (exists) {
        return prev.filter((item) => item.attireTag !== option.attireTag);
      }

      setQuantities((q) => ({
        ...q,
        [option.attireTag]: 1,
      }));

      return [
        ...prev,
        {
          ...option,
          purchaseType: "ATTIRE",
        },
      ];
    });
  };

  const handleSelectTicket = (ticket) => {
    setSelectedOptions((prev) => {
      const exists = prev.find((item) => item._id === ticket._id);

      return exists
        ? []
        : [
            {
              ...ticket,
              quantity: 1,
              purchaseType: "TICKET",
            },
          ];
    });
  };

  const handleDownload = (uri, e) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = uri;
    link.download = uri.split("/").pop();
    link.click();
  };

  const handleQuantityChange = (option, increment) => {
    const key = isAttireRequired ? option.attireTag : option.type;
    setQuantities((prev) => ({
      ...prev,
      [key]: Math.max(1, (prev[key] || 1) + (increment ? 1 : -1)),
    }));
  };

  const handleProceed = async () => {
    // free ticket flow
    if (isSelectedTicketFree()) {
      if (!selectedTicket || !user?._id) return;

      setIsClaimingFreeTicket(true);

      try {
        const response = await axios.post(
          `/events/${event._id}/invitations/free-ticket`,
          {
            guestId: user._id,
            ticketType: selectedTicket.type,
          },
        );

        onProceed({
          type: "ticket",
          options: [
            {
              ...selectedTicket,
              quantity: 1,
              paid: true,
              amountPaid: 0,
            },
          ],
          deliveryAddress: null,
          isFreeTicket: true,
        });

        if (response.data.status === "success") {
          setTimeout(() => {
            setIsClaimingFreeTicket(false);
            onClose();

            window.location.href = `/eventManagementDetails/${event._id}`;
          }, 800);
        }
      } catch (err) {
        console.error("Free ticket claim failed:", err);
        setIsClaimingFreeTicket(false);
        alert("Unable to claim free ticket. Please try again.");
      }

      return;
    }

    // paid ticket low
    setIsPaymentSummaryModalOpen(true);
  };

  const handlePay = () => {
    onProceed({
      type: isAttireRequired ? "attire" : "ticket",
      options: isAttireRequired
        ? selectedOptions.map((option) => ({
            ...option,
            quantity: quantities[option.attireTag] || 1,
          }))
        : selectedOptions, // ticket already has quantity: 1
      deliveryAddress: isAttireRequired ? deliveryAddress : null,
    });

    setIsPaymentSummaryModalOpen(false);
  };

  const calculateTotal = () => {
    if (!selectedOptions.length) return 0;

    return selectedOptions.reduce((total, option) => {
      const quantity = isAttireRequired
        ? quantities[option.attireTag] || 1
        : option.quantity || 1;

      return total + option.price * quantity;
    }, 0);
  };

  const formatCurrency = (amount) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;

    if (num === 0) {
      return "Free";
    }

    const symbol = preferredCurrency === "NGN" ? "₦" : "$";
    return `${symbol}${num.toLocaleString()}`;
  };

  const disableForAttire =
    isAttireRequired &&
    attireOptions.length > 0 &&
    (!selectedOptions.length || !deliveryAddress.trim());

  const disableForTicket =
    !isAttireRequired &&
    !selectedOptions.some((opt) => opt.purchaseType === "TICKET");

  const capitalizeWords = (text = "") =>
    text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

  if (!isOpen) return null;

  return (
    <>
      <div className="mandatory-purchase-modal-overlay" onClick={onClose}>
        <div
          className="mandatory-purchase-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="modal-close-button"
            type="button"
          >
            <span>×</span>
          </button>
          <div className="mandatory-purchase-modal-body">
            <h2 className="mandatory-purchase-title">
              {isAttireRequired
                ? "Select Mandatory Event Attire"
                : isTicketRequired
                  ? "Purchase Event Ticket"
                  : "Select Purchase Option"}
            </h2>
            <p className="mandatory-purchase-description">
              {isAttireRequired
                ? "Event attire is required for entry. Please purchase yours in advance. The host will arrange delivery."
                : "A ticket is required for entry. Please confirm your purchase."}
            </p>

            {isAttireRequired && attireOptions.length > 0 && (
              <div className="attire-options">
                {attireOptions.map((attire, index) => (
                  <div
                    key={index}
                    className={`attire-option ${
                      selectedOptions.some(
                        (item) => item.attireTag === attire.attireTag,
                      )
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleSelectOption(attire)}
                  >
                    <button
                      className="download-button"
                      onClick={(e) => handleDownload(attire.uri, e)}
                      type="button"
                    >
                      <img
                        src={"/images/download-icon.png"}
                        alt="Download"
                        className="download"
                      />
                      Download
                    </button>

                    {selectedOptions.some(
                      (item) => item.attireTag === attire.attireTag,
                    ) && <span className="check-marked">✓</span>}

                    <img
                      src={attire.uri || "/images/asoebi.png"}
                      alt={attire.attireTag}
                      className="mandatory-attire-image"
                    />
                    <p className="attire-tag">
                      {capitalizeWords(attire.attireTag)}
                    </p>
                    <p className="attire-price">
                      {formatCurrency(attire.price)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* {isTicketRequired && (
              <div className="ticket-options">
                {ticketOptions.map((ticket, index) => (
                  <div
                    key={index}
                    className={`ticket-option ${
                      selectedOptions.includes(ticket) ? "selected" : ""
                    }`}
                    onClick={() => handleSelectOption(ticket)}            
                  >
                    <img
                      src={ticket.imageUri || "/images/ticketVip.png"}
                      alt={ticket.type}
                      className="ticket-image"
                    />

                    <p>{ticket.type}</p>
                    <p>{formatCurrency(ticket.price)}</p>
                    {selectedOptions.includes(ticket) && (
                      <span className="check-marked">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )} */}

            {isTicketRequired && ticketOptions.length > 0 && (
              <div className="ticket-options">
                {ticketOptions.map((ticket, index) => {
                  const isSelected =
                    selectedTicket && selectedTicket._id === ticket._id;

                  const isFree = parseFloat(ticket.price) === 0;

                  return (
                    <div
                      key={ticket._id || ticket.type || index}
                      className={`ticket-option ${
                        isSelected ? "selected" : ""
                      } ${isFree ? "free-ticket" : ""}`}
                      onClick={() => handleSelectTicket(ticket)}
                    >
                      {isFree && !isSelected && (
                        <span className="free-badge">FREE</span>
                      )}

                      {isSelected && <span className="check-marked">✓</span>}

                      <img
                        src={ticket.imageUri || "/images/ticketVip.png"}
                        alt={ticket.type}
                        className="ticket-image"
                      />

                      <p className="ticket-type-name">{ticket.type}</p>
                      <p
                        className={`ticket-price ${isFree ? "free-price" : ""}`}
                      >
                        {formatCurrency(ticket.price)}
                      </p>
                      {isSelected ? (
                        <span className="check-marked">✓</span>
                      ) : (
                        ""
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {isAttireRequired &&
              selectedOptions.map((option, index) => (
                <div key={index} className="attire-details-row">
                  <h3 className="attire-tag">{option.attireTag}</h3>

                  {isAttireRequired && (
                    <div className="attire-price-container">
                      <div className="quantity-control">
                        <p className="attire-price">
                          {formatCurrency(
                            option.price * (quantities[option.attireTag] || 1),
                          )}
                        </p>

                        <button
                          type="button"
                          className={`quantity-button ${
                            quantities[option.attireTag] <= 1 ? "disabled" : ""
                          }`}
                          onClick={(e) => {
                            if (quantities[option.attireTag] <= 1) return;
                            handleQuantityChange(option, false, e);
                          }}
                        >
                          −
                        </button>

                        <span className="quantity-text">
                          {quantities[option.attireTag] || 1}
                        </span>
                        <button
                          type="button"
                          className="quantity-button"
                          onClick={() => handleQuantityChange(option, true)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

            {isAttireRequired && (
              <div className="delivery-address-container">
                <label className="delivery-address-label">
                  Delivery Address
                </label>
                <input
                  type="text"
                  placeholder="Enter delivery address"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  className="address-input"
                />
                <p className="mandatory-address-note">
                  Provide the address you want your attire to be delivered to
                </p>

                {refundPolicy && (
                  <p className="mandatory-refund-policy">
                    Refund Policy: {refundPolicy}
                  </p>
                )}
              </div>
            )}

            {isSelectedTicketFree() && (
              <div className="free-ticket-info">
                <p>
                  🎉 This is a free ticket! Click "Claim Free Ticket" to claim
                  it.
                </p>
              </div>
            )}

            <div className="mandatory-purchase-buttons">
              <button
                onClick={onPayLater}
                className="pay-later-button"
                type="button"
              >
                Pay Later
              </button>
              <button
                onClick={handleProceed}
                className="mandatory-proceed-button"
                type="button"
                disabled={
                  isClaimingFreeTicket || disableForAttire || disableForTicket
                }
              >
                {isClaimingFreeTicket
                  ? "Claiming..."
                  : isSelectedTicketFree()
                    ? "Claim Free Ticket"
                    : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PaymentSummaryModal
        isOpen={isPaymentSummaryModalOpen}
        onClose={() => setIsPaymentSummaryModalOpen(false)}
        onBack={() => setIsPaymentSummaryModalOpen(false)}
        onPay={handlePay}
        event={event}
        selectedOptions={selectedOptions}
        deliveryAddress={isAttireRequired ? deliveryAddress : null}
        quantities={quantities}
        isAttirePurchase={isAttireRequired}
        applyServiceCharge={isAttireRequired}
        user={user}
      />
    </>
  );
};

export default MandatoryPurchaseModal;
